import { memo, type ReactNode } from "react";
import Slider from "react-slick";
import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import CourseBanner from "./courses/courseBanner";
import CoursesCard from "./courses/coursesCards";
import CourseDetailSkeleton from "./CourseDetailSkeleton";
import { UP_NEXT_SETTINGS } from "../constants/sliderSettings";
import { Config } from "../constants/Config";
import tagManagerEvents from "../utils/GoogleTagManager";
import type { Chapter, CourseVideo } from "../types/courseDetail";
import type { CourseDetailState } from "../hooks/useCourseDetail";

const COURSES_THUMB = Config.COURSES_THUMBNAIL;
const COURSES_THUMB_NEW = Config.COURSES_THUMBNAIL_NEW;

export type CourseDetailProps = CourseDetailState;

/* -------------------------------------------------------------------------- */
/* Search results                                                             */
/* -------------------------------------------------------------------------- */

const SearchResultsPanel = ({ children }: { children: ReactNode }) => (
  <div className="position-relative mb-4">
    <div className="w-100 courses-bg pb-3 mh-300px overflow-auto mt-4 br-10px">
      <div className="courses-bg p-3 m-2 mb-0">
        <h4>Search Results</h4>
      </div>
      <div className="row m-0 p-3 pt-0">{children}</div>
    </div>
  </div>
);

const NoResults = ({ searchTerm }: { searchTerm: string }) => (
  <div className="position-relative w-100 mb-4">
    <div className="w-100 courses-bg pb-3 mt-4 br-10px mh-100vh overflow-auto">
      <div className="courses-bg p-3 m-2">
        <h4>Search Results</h4>
      </div>
      <small className="p-3">There are no results related to "{searchTerm}"</small>
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Chapter sidebar (topic / sub-topic tree)                                   */
/* -------------------------------------------------------------------------- */

const SidebarVideoRow = ({
  video,
  active,
  onSelect,
}: {
  video: CourseVideo;
  active: boolean;
  onSelect: (id: number) => void;
}) => (
  <div
    onClick={() => onSelect(video.id)}
    className={`d-flex ms-3 _hover_red cursor-pointer ${active ? "active" : ""}`}
  >
    <div>
      <FontAwesomeIcon icon={regular("circle-play")} />
    </div>
    <div className="ms-2">
      <p>{video.title}</p>
    </div>
  </div>
);

const ChapterSidebar = ({
  chapter,
  activeVideoId,
  onSelectVideo,
}: {
  chapter: Chapter;
  activeVideoId: number;
  onSelectVideo: (id: number) => void;
}) => (
  <div className="w-100 courses-bg pb-3 mt-4 position-relative br-10px mh-100vh overflow-auto">
    <div className="p-3">
      <h5 className="mb-4">
        {`Chapter ${String(chapter.chapterNumber).padStart(2, "0")}`}
      </h5>
      {chapter.topics.map((topic) =>
        topic.videos.length > 0 ? (
          <div key={topic.id}>
            <h6>{topic.title}</h6>
            {topic.videos.map((video) => (
              <SidebarVideoRow
                key={video.id}
                video={video}
                active={activeVideoId === video.id}
                onSelect={onSelectVideo}
              />
            ))}
          </div>
        ) : (
          <div key={topic.id}>
            <h6>{topic.title}</h6>
            {topic.subTopic.map((subTopic) =>
              subTopic.videos.length > 0 ? (
                <div key={subTopic.id}>
                  <h6 className="d-flex ms-3">{subTopic.title}</h6>
                  {subTopic.videos.map((video) => (
                    <SidebarVideoRow
                      key={video.id}
                      video={video}
                      active={activeVideoId === video.id}
                      onSelect={onSelectVideo}
                    />
                  ))}
                </div>
              ) : null
            )}
          </div>
        )
      )}
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Course video player + "Up Next"                                            */
/* -------------------------------------------------------------------------- */

const CourseVideoPlayer = ({
  chapter,
  utmSource,
  onToggleBookmark,
}: {
  chapter: Chapter;
  utmSource: string;
  onToggleBookmark: (chapter: Chapter) => void;
}) => {
  const video = chapter.video;
  if (!video) {
    return null;
  }
  return (
    <div className="w-100 p-3 my-4 courses-bg br-10px">
      <video
        src={video.url.replace(COURSES_THUMB, COURSES_THUMB_NEW)}
        controls
        className="w-100"
      />
      <div className="w-100 mt-3">
        <div className="d-flex justify-content-between mb-3 flex-md-row flex-column align-items-start align-items-md-center">
          <div>
            <h6>{video.title}</h6>
          </div>
          <div className="d-flex align-items-center justify-content-end">
            <FontAwesomeIcon
              onClick={() => onToggleBookmark(chapter)}
              icon={video.bookmark ? solid("heart") : regular("heart")}
              className="text-red cursor-pointer me-2"
            />
            {video.textbookUrl && (
              <a
                href={video.textbookUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm _hover text-white btn-br bg-red"
              >
                <FontAwesomeIcon icon={solid("book")} />
                Text Book
              </a>
            )}
            {video.questions !== undefined && video.questions > 0 && (
              <a
                href={
                  video.topicId !== undefined
                    ? `/quiz?topicId=${video.topicId}`
                    : `/quiz?topicId=${video.subTopicId}`
                }
                onClick={() => tagManagerEvents("quiz_opened", utmSource)}
                className="btn btn-sm _hover text-white btn-br bg-red ms-2"
              >
                <FontAwesomeIcon icon={solid("lightbulb")} className="me-1" />
                Quiz
              </a>
            )}
          </div>
        </div>
        <div className="text-break w-100">
          <p className="small text-muted text-break text-turncate">
            {video.topicTitle}
          </p>
        </div>
      </div>
    </div>
  );
};

const CourseUpNext = ({
  videos,
  onSelectVideo,
}: {
  videos: CourseVideo[];
  onSelectVideo: (id: number) => void;
}) => {
  if (videos.length === 0) {
    return null;
  }
  return (
    <>
      <h5 className="fw-bold">Up Next</h5>
      <Slider {...UP_NEXT_SETTINGS} className="upnext">
        {videos.map((item) => (
          <div
            key={item.id}
            onClick={() => onSelectVideo(item.id)}
            className="card border-0 shadow cursor-pointer br-10px h-100 my-3"
            style={{ width: "18rem" }}
          >
            <img src={item.thumbnail} className="card-img-top" alt="" />
            <div className="card-body">
              <div className="row my-1">
                <div className="col-8 pe-0">
                  <Tooltip title={item.topicTitle ?? ""}>
                    <p className="small text-truncate text-muted">
                      {item.topicTitle}
                    </p>
                  </Tooltip>
                </div>
              </div>
              <Tooltip title={item.title}>
                <p className="card-text font-15 my-3 text-truncate">
                  {item.title}
                </p>
              </Tooltip>
            </div>
          </div>
        ))}
      </Slider>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/* Course detail screen UI                                                    */
/* -------------------------------------------------------------------------- */

const CourseDetail = ({
  isLoading,
  courseId,
  chapterId,
  courseVideoId,
  utmSource,
  token,
  topics,
  currentVideo,
  currentCollar,
  nextCollar,
  searchCollar,
  searchCourseVideo,
  searchTerm,
  showError,
  setSearchTerm,
  runSearch,
  toggleCourseBookmark,
  toggleCollarBookmark,
}: CourseDetailProps) => {
  if (isLoading) {
    return <CourseDetailSkeleton />;
  }

  const goToCourseVideo = (videoId: number): void => {
    window.location.href = `/course_detail?courseId=${courseId}&chapterId=${chapterId}&courseVideoId=${videoId}`;
  };

  const bannerHeading = currentCollar
    ? currentCollar.title
    : topics
    ? `Lesson ${String(topics.chapterNumber).padStart(2, "0")}`
    : "";
  const bannerDesc = currentCollar
    ? currentCollar.description
    : topics
    ? topics.title
    : "";

  return (
    <>
      <CourseBanner heading={bannerHeading} desc={bannerDesc} />
      <div className="w-100 my-4 course_details">
        <div className="container-xxl">
          <div className="row gx-0 gx-lg-5">
            {!showError && (
              <div className="col-12 col-md-4 d-none d-lg-block">
                <div className="input-group course-details position-relative br-10px">
                  <input
                    type="text"
                    className="form-control small py-2 border-0"
                    onChange={(event) => setSearchTerm(event.target.value)}
                    value={searchTerm}
                    placeholder={currentCollar ? "Search Video" : "Search Topic"}
                  />
                  <span
                    className="input-group-text cursor-pointer border-0"
                    onClick={() => runSearch(searchTerm)}
                  >
                    <FontAwesomeIcon className="text-grey" icon={solid("search")} />
                  </span>
                </div>
              </div>
            )}

            <div className="col-12 col-md-12 col-lg-8">
              {!showError && (
                <div className="w-100 py-3 courses-bg br-10px">
                  {currentCollar ? (
                    <ul className="breadcrums">
                      <li>{currentCollar.course.title}</li>
                      <li>{currentCollar.title}</li>
                    </ul>
                  ) : (
                    topics && (
                      <ul className="breadcrums">
                        <li>Grade</li>
                        <li>{topics.level.title}</li>
                        <li>
                          {`Lesson ${String(topics.chapterNumber).padStart(2, "0")}`}
                        </li>
                      </ul>
                    )
                  )}
                </div>
              )}
            </div>

            {searchCourseVideo.length > 0 ? (
              <SearchResultsPanel>
                {searchCourseVideo.map((item) => (
                  <div className="col-12 col-sm-6 col-md-3 col-lg-4" key={item.id}>
                    <a
                      href={`/course_detail?courseId=${courseId}&chapterId=${chapterId}&courseVideoId=${item.id}`}
                      className="text-dark _hover"
                    >
                      <div className="w-100 my-3 br-10px overflow-hidden d-flex bg-light">
                        <div className="collarSearch_img_width">
                          <img src={item.thumbnail} className="w-100" alt="" />
                        </div>
                        <div className="w-75">
                          <div>
                            <p className="text-muted mx-2 mt-1 mb-0 small text-truncate w-90">
                              {item.topicTitle}
                            </p>
                          </div>
                          <div>
                            <p className="text-truncate mx-2 w-90">{item.title}</p>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                ))}
              </SearchResultsPanel>
            ) : chapterId && searchTerm ? (
              <NoResults searchTerm={searchTerm} />
            ) : null}

            <div className="col-12 col-md-4 d-none d-lg-block">
              {topics && (
                <ChapterSidebar
                  chapter={topics}
                  activeVideoId={courseVideoId}
                  onSelectVideo={goToCourseVideo}
                />
              )}
            </div>

            <div className="col-12 col-md-12 col-lg-8">
              {currentVideo?.video && (
                <CourseVideoPlayer
                  chapter={currentVideo}
                  utmSource={utmSource}
                  onToggleBookmark={toggleCourseBookmark}
                />
              )}

              {currentVideo?.nextVideo !== undefined && (
                <div className="w-100 my-4">
                  <CourseUpNext
                    videos={currentVideo.nextVideo}
                    onSelectVideo={goToCourseVideo}
                  />
                </div>
              )}
            </div>

            {searchCollar.length > 0 ? (
              <SearchResultsPanel>
                {searchCollar.map((item) => (
                  <div className="col-12 col-sm-6 col-md-3 col-lg-4" key={item.id}>
                    <a
                      href={`/course_detail?courseId=${item.course.id}&videoId=${item.id}`}
                      className="text-dark _hover"
                    >
                      <div className="w-100 my-3 br-10px overflow-hidden d-flex bg-light">
                        <div className="collarSearch_img_width">
                          <img
                            src={item.thumbnail.replace(
                              COURSES_THUMB,
                              COURSES_THUMB_NEW
                            )}
                            className="w-100"
                            alt=""
                          />
                        </div>
                        <div className="w-75">
                          <div>
                            <p className="text-muted mx-2 mt-1 mb-0 small text-truncate w-90">
                              {item.title}
                            </p>
                          </div>
                          <div>
                            <p className="text-truncate mx-2 w-90">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                ))}
              </SearchResultsPanel>
            ) : !chapterId && searchTerm ? (
              <NoResults searchTerm={searchTerm} />
            ) : null}

            {currentCollar ? (
              <div className="col-12">
                <div className="w-100 p-3 my-4 courses-bg br-10px">
                  <iframe
                    height="476px"
                    width="100%"
                    className="br-10px"
                    src={`https://www.youtube.com/embed/${
                      currentCollar.live_url.split("v=")[
                        currentCollar.live_url.split("v=").length - 1
                      ]
                    }?autoplay=0&&controls=1&rel=1`}
                    title={currentCollar.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <div className="w-100 mt-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6>{currentCollar.title}</h6>
                      <FontAwesomeIcon
                        className="text-red cursor-pointer"
                        onClick={toggleCollarBookmark}
                        icon={
                          currentCollar.bookmark
                            ? solid("heart")
                            : regular("heart")
                        }
                      />
                    </div>
                    <p className="text-break">{currentCollar.description}</p>
                  </div>
                </div>
                <div className="w-100 my-4">
                  {nextCollar.length > 0 && (
                    <>
                      <h5 className="fw-bold">Up Next</h5>
                      <Slider {...UP_NEXT_SETTINGS} className="upnext">
                        {nextCollar.map((item) => (
                          <div className="my-2" key={item.id}>
                            <CoursesCard
                              data={item}
                              courseId={item.course.id}
                              courseName={item.course.title}
                              utm_source={utmSource}
                              token={token}
                            />
                          </div>
                        ))}
                      </Slider>
                    </>
                  )}
                </div>
              </div>
            ) : (
              showError && (
                <div className="text-center mt-5">
                  <p className="fw-bold mt-5 font-31">No data found</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(CourseDetail);
